import Header from "@/components/header";
import useIsSmall from "@/hooks/useIsSmall";
import { ServiceType, SmsRequestDto, SmsRequestStatus } from "@/models/club";
import MessageText from "@/modules/marketing/MessageText";
import { useGetSmsPlaceHolder } from "@/services/smsPlaceHolders/hooks/useGetSmsPlaceHolder";
import useMessageList from "@/store/messageList";
import { Alert, Button, Loader, Skeleton } from "@mantine/core";
import { IconDiscount2, IconMapPin, IconMessage, IconPlus } from "@tabler/icons-react";
import moment from "moment-jalaali";
import { useCallback, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import useGetMessagesRequest from "./hooks/useGetMessagesRequest";
import classes from "./messages.module.css";
import Recipients from "./Recipients";

const fa = (value?: number) => (value ?? 0).toLocaleString("fa-IR");

const types: Record<number, { label: string; icon: typeof IconMessage; tone: string }> = {
  [ServiceType.Value3]: { label: "پیامک به مشتریان", icon: IconMessage, tone: "violet" },
  [ServiceType.Value2]: { label: "پیامک تخفیف", icon: IconDiscount2, tone: "green" },
  [ServiceType.Value1]: { label: "پیامک منطقه‌ای", icon: IconMapPin, tone: "orange" },
};
const statuses: Record<number, { label: string; tone: string }> = {
  [SmsRequestStatus.Value0]: { label: "در انتظار بررسی", tone: "pending" },
  [SmsRequestStatus.Value1]: { label: "ارسال شد", tone: "ok" },
  [SmsRequestStatus.Value2]: { label: "ناموفق", tone: "failed" },
};
const filters: { value?: ServiceType; label: string }[] = [
  { label: "همه" },
  { value: ServiceType.Value3, label: "به مشتریان" },
  { value: ServiceType.Value2, label: "تخفیف" },
  { value: ServiceType.Value1, label: "منطقه‌ای" },
];

// When the send was made: «امروز ۱۴:۲۰», «دیروز …», or the Persian date.
function when(value?: string) {
  if (!value) return "";
  const date = moment(value);
  const time = date.format("HH:mm");
  if (date.isSame(moment(), "day")) return `امروز ${time}`;
  if (date.isSame(moment().subtract(1, "day"), "day")) return `دیروز ${time}`;
  return `${date.format("jYYYY/jMM/jDD")} · ${time}`;
}

// The list also carries the text as the first recipient received it (not yet in the generated client).
type SendItem = SmsRequestDto & { sampleMessage?: string | null; sampleRecipientName?: string | null };

function SendCard({ item }: { item: SendItem }) {
  const tags = useGetSmsPlaceHolder().data ?? [];
  const [open, setOpen] = useState(false);
  const [showRecipients, setShowRecipients] = useState(false);
  const type = types[item.serviceType ?? ServiceType.Value3] ?? types[ServiceType.Value3];
  const status = statuses[item.status ?? SmsRequestStatus.Value1];
  const Icon = type.icon;
  return (
    <article className={classes.card}>
      <div className={classes.cardHead}>
        <span className={classes.icon} data-tone={type.tone}><Icon size={20} stroke={1.7} /></span>
        <div className={classes.cardTitle}>
          <strong>{type.label}</strong>
          <span>{when(item.creationTime)}</span>
        </div>
        {status && <span className={classes.status} data-tone={status.tone}>{status.label}</span>}
      </div>
      {item.sampleMessage && <span className={classes.sampleLabel}>متن ارسالی{item.sampleRecipientName ? ` برای ${item.sampleRecipientName}` : ""}</span>}
      <button type="button" className={classes.message} data-open={open} onClick={() => setOpen(!open)} aria-expanded={open}>
        {item.sampleMessage ? item.sampleMessage : <MessageText text={item.message ?? ""} tags={tags} />}
      </button>
      {item.status === SmsRequestStatus.Value2 && item.adminNote && <p className={classes.note}>{item.adminNote}</p>}
      <dl className={classes.meta}>
        <div><dt>گیرندگان</dt><dd>{item.serviceType !== ServiceType.Value1 && item.id ? <button type="button" className={classes.recipientsLink} onClick={() => setShowRecipients(true)}>{item.sendToAll ? "همه" : `${fa(item.itemsCount)} نفر`} · دیدن</button> : item.sendToAll ? "همه" : `${fa(item.itemsCount)} نفر`}</dd></div>
        <div><dt>پیامک</dt><dd>{fa(item.smsCount)}</dd></div>
        <div><dt>هزینه</dt><dd>{fa(item.sumAmount)} ریال</dd></div>
      </dl>
      {showRecipients && item.id && <Recipients id={item.id} onClose={() => setShowRecipients(false)} />}
    </article>
  );
}

// SMS history: what went out, to how many, at what cost, and whether it was sent.
const Messages = () => {
  const { serviceType, setServiceType } = useMessageList();
  const isSmall = useIsSmall();
  const navigate = useNavigate();
  const { state } = useLocation();
  const { data, hasNextPage, fetchNextPage, isFetchingNextPage, isLoading } = useGetMessagesRequest({ params: { Count: 10, ServiceType: serviceType } });
  const observer = useRef<IntersectionObserver | null>(null);
  const lastRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (isFetchingNextPage) return;
      observer.current?.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasNextPage) fetchNextPage();
      });
      if (node) observer.current.observe(node);
    },
    [isFetchingNextPage, fetchNextPage, hasNextPage],
  );
  const items = data?.pages.flatMap((page) => page?.result ?? []) ?? [];
  const newButton = (
    <Button color="violet" leftSection={<IconPlus size={16} />} onClick={() => navigate("/dashboard/sendMessage")} size={isSmall ? "xs" : "sm"}>
      پیامک جدید
    </Button>
  );

  return (
    <>
      {isSmall && <Header title="تاریخچه پیامک‌ها" showBack customComponent={newButton} />}
      <main className={classes.page} dir="rtl">
        {!isSmall && (
          <header className={classes.head}>
            <div>
              <h1>تاریخچه پیامک‌ها</h1>
              <p>همه‌ی ارسال‌ها، تعداد گیرنده‌ها، هزینه و وضعیتشان.</p>
            </div>
            {newButton}
          </header>
        )}
        {state?.campaignSent && <Alert color="green" title="درخواست ارسال ثبت شد" radius="md">وضعیت ارسال را همین‌جا دنبال کنید.</Alert>}

        <div className={classes.filters} role="group" aria-label="نوع پیامک">
          {filters.map((filter) => (
            <button key={filter.label} type="button" className={classes.chip} aria-pressed={serviceType === filter.value || (!serviceType && !filter.value)} onClick={() => setServiceType(filter.value)}>
              {filter.label}
            </button>
          ))}
        </div>

        {isLoading ? (
          <div className={classes.list}>{[0, 1, 2].map((i) => <Skeleton key={i} height={170} radius={16} />)}</div>
        ) : items.length ? (
          <div className={classes.list}>
            {items.map((item, index) => (
              <div key={item.id ?? index} ref={index === items.length - 1 ? lastRef : undefined}>
                <SendCard item={item} />
              </div>
            ))}
          </div>
        ) : (
          <div className={classes.empty}>
            <IconMessage size={32} stroke={1.5} />
            <strong>{serviceType ? "پیامکی از این نوع نفرستاده‌اید" : "هنوز پیامکی نفرستاده‌اید"}</strong>
            <span>اولین پیامک را بفرستید؛ مشتری‌ها را دعوت کنید یا کد تخفیف بدهید.</span>
            {newButton}
          </div>
        )}
        {isFetchingNextPage && <div className={classes.more}><Loader type="dots" color="violet" /></div>}
      </main>
    </>
  );
};

export default Messages;
